import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAYMENT_METHOD_LABEL, partnerArApi, type PaymentMethod } from "@/services/partnerAr";
import { formatCents } from "@/services/finance";
import { invalidateCredit } from "./PartnerCreditOverview";

/**
 * 登记收款（核销）— 样稿 credit-mockup-v1 ③。权限 finance.receivables.manage（按钮处已用 <Can> 包住）。
 * 默认「自动」：不传 allocations，后端按到期日从早到晚抵扣、先用掉预收余额（可关）、多余记预收余额。
 * 勾「手动指定分摊」：每笔应收单独填本次抵扣（预填一份从早到晚的建议），后端再校验（每笔 ≤ 未收、合计 ≤ 收款金额）。
 */
const toCents = (v: string): number => Math.round((Number(v.replace(/[$,\s]/g, "")) || 0) * 100);
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function RegisterPaymentModal({ partnerId, partnerName, onClose }: { partnerId: string; partnerName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const openQ = useQuery({ queryKey: ["b2b", "receivables", partnerId, "open-for-payment"], queryFn: () => partnerArApi.receivables(partnerId, "open") });
  const creditQ = useQuery({ queryKey: ["b2b", "credit", partnerId], queryFn: () => partnerArApi.credit(partnerId) });
  const rows = useMemo(() => (openQ.data?.rows ?? []).filter((r) => r.outstanding_cents > 0), [openQ.data]);
  const prepaid = creditQ.data?.prepaid_balance_cents ?? 0;

  const [amount, setAmount] = useState("");
  const [receivedOn, setReceivedOn] = useState(localToday());
  const [method, setMethod] = useState<PaymentMethod>("wire");
  const [reference, setReference] = useState("");
  const [manual, setManual] = useState(false);
  const [usePrepaid, setUsePrepaid] = useState(true);
  const [alloc, setAlloc] = useState<Record<string, string>>({});

  const amountCents = toCents(amount);
  const allocCents = rows.map((r) => ({ id: r.id, cents: toCents(alloc[r.id] ?? "") }));
  const allocSum = allocCents.reduce((a, x) => a + x.cents, 0);
  const overRow = rows.find((r) => toCents(alloc[r.id] ?? "") > r.outstanding_cents);

  function suggest() {                                    // 预填：从早到晚填满（仅是建议，可逐笔改）
    let left = amountCents;
    const next: Record<string, string> = {};
    for (const r of rows) {
      const take = Math.min(left, r.outstanding_cents);
      next[r.id] = take > 0 ? (take / 100).toFixed(2) : "";
      left -= take;
    }
    setAlloc(next);
  }

  const save = useMutation({
    mutationFn: () =>
      partnerArApi.registerPayment(partnerId, {
        amount_cents: amountCents, received_on: receivedOn, method, reference: reference.trim() || undefined,
        ...(manual
          ? { allocations: allocCents.filter((x) => x.cents > 0).map((x) => ({ receivable_id: x.id, amount_cents: x.cents })) }
          : { use_prepaid: usePrepaid }),
      }),
    onSuccess: () => { invalidateCredit(qc, partnerId); onClose(); },
    onError: (e) => alert((e as Error).message || "登记收款失败"),
  });

  const manualBad = manual && (allocSum > amountCents || !!overRow);
  const canSave = amountCents > 0 && !!receivedOn && !manualBad && !save.isPending;
  const field = "h-9 w-full rounded-lg border border-slate-200 px-2.5 text-[13px]";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/45 p-4 pt-[8vh]" onClick={onClose}>
      <div role="dialog" aria-label="登记收款" data-testid="payment-modal" className="w-full max-w-[640px] rounded-[14px] bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-[17px] font-semibold text-slate-900">登记收款 · {partnerName}</h4>
        <div className="mb-3 text-[12.5px] text-slate-600">保存后应收变「已收 / 部分收」，额度立即回来；逾期付清后自动解除停单。</div>

        <div className="mb-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">收款金额（USD）
            <input className={field} inputMode="decimal" placeholder="3,500.00" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="收款金额" />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">收款日期
            <input className={field} type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} aria-label="收款日期" />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">方式
            <select className={`${field} bg-white`} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} aria-label="收款方式">
              {(Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[]).map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] text-slate-600">流水号 / 备注
            <input className={field} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="WF-88213" aria-label="流水号" />
          </label>
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-4 text-[12.5px] text-slate-600">
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={manual} onChange={(e) => { setManual(e.target.checked); if (e.target.checked) suggest(); }} /> 手动指定分摊（默认自动按到期日从早到晚抵扣）</label>
          {!manual && prepaid > 0 && (
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={usePrepaid} onChange={(e) => setUsePrepaid(e.target.checked)} /> 先用掉预收余额 {formatCents(prepaid)}</label>
          )}
        </div>

        <div className="max-h-[260px] overflow-auto rounded-[10px] border border-slate-200">
          <table className="w-full min-w-[520px] text-[13px] tabular-nums">
            <thead className="bg-slate-50 text-[12px] font-medium text-slate-400">
              <tr><th className="px-2.5 py-2 text-left">订单</th><th className="px-2.5 py-2 text-left">到期</th><th className="px-2.5 py-2 text-right">未收</th><th className="px-2.5 py-2 text-right">本次抵扣</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-2.5 py-1.5">{r.order_no ?? r.receivable_number}</td>
                  <td className="px-2.5 py-1.5">{r.due_date.slice(5)}{r.overdue ? "（逾期）" : ""}</td>
                  <td className="px-2.5 py-1.5 text-right">{formatCents(r.outstanding_cents)}</td>
                  <td className="px-2.5 py-1.5 text-right">
                    {manual
                      ? <input className="h-8 w-[96px] rounded-lg border border-slate-200 px-2 text-right text-[13px]" inputMode="decimal" value={alloc[r.id] ?? ""} onChange={(e) => setAlloc({ ...alloc, [r.id]: e.target.value })} aria-label={`抵扣 ${r.order_no ?? r.receivable_number}`} />
                      : <span className="text-slate-400">自动</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-5 text-center text-slate-400">没有未收的应收；收款会全部记为「预收余额」</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-1.5 text-[11.5px] text-slate-400" data-testid="alloc-summary">
          {manual
            ? <>合计抵扣 <b className={manualBad ? "text-rose-600" : "text-slate-700"}>{formatCents(allocSum)}</b> / 收款金额 {formatCents(amountCents)}{allocSum < amountCents && amountCents > 0 && !manualBad ? ` · 多出的 ${formatCents(amountCents - allocSum)} 记「预收余额」` : ""}{allocSum > amountCents ? " · 合计超过收款金额" : ""}{overRow ? ` · ${overRow.order_no ?? overRow.receivable_number} 抵扣超过未收` : ""}</>
            : <>收款大于全部未收时，多出部分记「预收余额」；先用掉预收余额再分摊新收款（可关）</>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-slate-300 px-4 text-[13px] text-slate-600">取消</button>
          <button type="button" disabled={!canSave} onClick={() => save.mutate()} data-testid="payment-save" className="h-9 rounded-lg bg-blue-600 px-4 text-[13px] font-semibold text-white disabled:opacity-40">{save.isPending ? "保存中…" : "保存收款"}</button>
        </div>
      </div>
    </div>
  );
}
